/* IMPROVEMENT LOOP — errors and feedback (Jay, System Project tasks #1137 / #1138, 02/11). Admin only: the raw rows (the server
   checks Admin as well as the grant). Errors: one row per error, screen and role, with its count, first and last seen; mark it
   known, fixed (it comes back as new if it happens again) or ignored (left out of the 16:00 report). Feedback: what people sent,
   with a status and an answer the sender sees under "My feedback"; each has its own number (FB-0001, since 03/11).
   Settings: the config table — what is logged and collected. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, MessageSquarePlus, SlidersHorizontal, RefreshCw, Paperclip, ExternalLink, X } from 'lucide-react';
import Header from './Header';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { BACKEND_URL } from './backendConfig';
import { fmtDateTimeVN } from './timeZone';
import './style.css';
import './ProjectApproval.css';
import './SPHub.css';
import './Feedback.css';

const API = `${BACKEND_URL}/api/loop/admin`;
const errOf = (e) => e?.response?.data?.error || e.message;
const ES = { new: 'xLoop.esNew', known: 'xLoop.esKnown', fixed: 'xLoop.esFixed', ignored: 'xLoop.esIgnored' };
const FS = { new: 'xLoop.stNew', seen: 'xLoop.stSeen', planned: 'xLoop.stPlanned', done: 'xLoop.stDone', wont: 'xLoop.stWont' };
const FK = { bug: 'xLoop.fbBug', hard_to_use: 'xLoop.fbHard', idea: 'xLoop.fbIdea' };

function ErrorsTab({ envSel }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const load = useCallback(() => axios.get(`${API}/errors`, { params: { days, env: envSel } })
    .then((r) => { setRows(r.data.rows); setErr(''); }).catch((e) => setErr(errOf(e))), [days, envSel]);
  useEffect(() => { setRows(null); load(); }, [load]);
  const columns = useMemo(() => [
    { key: 'status', get: (x) => t(ES[x.status] || x.status) },
    { key: 'source', get: (x) => (x.source === 'screen' ? t('xLoop.srcScreen') : t('xLoop.srcServer')) },
    { key: 'kind', get: (x) => x.kind },
    { key: 'message', get: (x) => x.message },
    { key: 'screen', get: (x) => x.screen || t('xLoop.server') },
    { key: 'role', get: (x) => x.role || '—' },
    { key: 'hits', get: (x) => Number(x.hits) },
    { key: 'firstSeen', get: (x) => x.firstSeen },
    { key: 'lastSeen', get: (x) => x.lastSeen },
  ], [t]);
  const tools = useColumnTools(rows || [], columns);
  const mark = async (x, status) => {
    try { await axios.put(`${API}/errors/${x.id}`, { status }); setRows((list) => list.map((y) => (y.id === x.id ? { ...y, status } : y))); }
    catch (e) { setErr(errOf(e)); }
  };
  const toggle = (id) => setOpen((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <div data-il-errors>
      <div className="il-bar">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label={t('xLoop.days', { n: days })} data-il-days>
          {[1, 7, 30, 90].map((n) => <option key={n} value={n}>{t('xLoop.days', { n })}</option>)}
        </select>
        <button type="button" className="sp-btn" onClick={load}><RefreshCw size={13} /> {t('xLoop.refresh')}</button>
        {tools.active && <button type="button" className="sp-btn" onClick={tools.clear}>{t('xLoop.clearFilters')}</button>}
        <span className="il-shown">{rows ? t('xLoop.shownOf', { n: tools.shown.length, m: rows.length }) : ''}</span>
      </div>
      {err && <p className="sp-error">{err}</p>}
      {rows && !rows.length ? <p className="sp-sub">{t('xLoop.noErrors')}</p> : (
        <div className="il-scroll">
          <table className="sp-table il-table">
            <thead><tr>
              <SortTh label={t('xLoop.colStatus')} {...tools.thProps('status')} />
              <SortTh label={t('xLoop.colSource')} {...tools.thProps('source')} />
              <SortTh label={t('xLoop.colMessage')} {...tools.thProps('message')} />
              <SortTh label={t('xLoop.colScreen')} {...tools.thProps('screen')} />
              <SortTh label={t('xLoop.colRole')} {...tools.thProps('role')} />
              <SortTh label={t('xLoop.colHits')} className="num" {...tools.thProps('hits')} />
              <SortTh label={t('xLoop.colFirst')} {...tools.thProps('firstSeen')} />
              <SortTh label={t('xLoop.colLast')} {...tools.thProps('lastSeen')} />
              <th>{t('xLoop.colAction')}</th>
            </tr></thead>
            <tbody>
              {(rows ? tools.shown : []).map((x) => (
                <tr key={x.id} data-il-err={x.id}>
                  <td><span className={`il-chip ${x.status}`}>{t(ES[x.status] || x.status)}</span></td>
                  <td className="il-nowrap"><span className="il-src">{x.source === 'screen' ? t('xLoop.srcScreen') : t('xLoop.srcServer')}</span><br /><small>{x.kind}</small></td>
                  <td className="il-msg">
                    {x.message}
                    {x.api && <div><small><code>{x.api}</code></small></div>}
                    {(x.detail || x.lastUserId) && <button type="button" className="sp-btn" style={{ marginTop: 4 }} onClick={() => toggle(x.id)} aria-expanded={open.has(x.id)}>{t('xLoop.detail')}</button>}
                    {open.has(x.id) && <pre>{[x.detail, x.lastUserId ? `${t('xLoop.colLastUser')}: ${x.lastUser || ''} (#${x.lastUserId})` : ''].filter(Boolean).join('\n\n')}</pre>}
                  </td>
                  <td className="il-screen"><code>{x.screen || t('xLoop.server')}</code></td>
                  <td>{x.role || '—'}</td>
                  <td className="num"><b>{Number(x.hits).toLocaleString()}</b></td>
                  <td className="il-nowrap">{fmtDateTimeVN(x.firstSeen)}</td>
                  <td className="il-nowrap">{fmtDateTimeVN(x.lastSeen)}</td>
                  <td className="il-act">
                    {x.status !== 'known' && <button type="button" className="sp-btn" onClick={() => mark(x, 'known')} data-il-mark="known">{t('xLoop.markKnown')}</button>}
                    {x.status !== 'fixed' && <button type="button" className="sp-btn primary" onClick={() => mark(x, 'fixed')} data-il-mark="fixed">{t('xLoop.markFixed')}</button>}
                    {x.status !== 'ignored' && <button type="button" className="sp-btn" onClick={() => mark(x, 'ignored')} data-il-mark="ignored">{t('xLoop.markIgnore')}</button>}
                    {x.status !== 'new' && <button type="button" className="sp-btn" onClick={() => mark(x, 'new')} data-il-mark="new">{t('xLoop.markNew')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* THE FEEDBACK, laid out for answering (Max 03/11: "cần thiết kế lại cho hợp lý", "task cũng tự đánh số … đưa lên cột đầu",
   "vùng answer đang bị nhỏ quá"): its number first (FB-0001, issued by the server), who sent it and when in one cell, the type as
   a coloured mark, the note with its files and the screen under it, the status (saved as soon as it is picked), and a wide
   answer box with Save under it. Pills above count each status and filter by it. */
function FeedbackTab({ envSel }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState({});     // id → answer being written
  const [saved, setSaved] = useState(null);
  const [statusF, setStatusF] = useState('');
  const [preview, setPreview] = useState(null);   // an attached image, large over the page (Max 03/11: "có đính kèm thì làm sao xem")
  useEffect(() => {
    if (!preview) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setPreview(null); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [preview]);
  const load = useCallback(() => axios.get(`${API}/feedback`, { params: { env: envSel } })
    .then((r) => { setRows(r.data.rows); setErr(''); }).catch((e) => setErr(errOf(e))), [envSel]);
  useEffect(() => { setRows(null); load(); }, [load]);
  const columns = useMemo(() => [
    { key: 'no', get: (x) => x.no || 0 },
    { key: 'sender', get: (x) => x.sender || '' },
    { key: 'kind', get: (x) => t(FK[x.kind] || x.kind) },
    { key: 'note', get: (x) => x.note || '' },
    { key: 'status', get: (x) => t(FS[x.status] || x.status) },
  ], [t]);
  const tools = useColumnTools(rows || [], columns);
  const shown = (rows ? tools.shown : []).filter((x) => !statusF || x.status === statusF);
  const count = (st) => (rows || []).filter((x) => x.status === st).length;
  const save = async (x, patch) => {
    const d = { status: x.status, reply: x.reply || '', taskRef: x.taskRef || '', ...(draft[x.id] != null ? { reply: draft[x.id] } : {}), ...patch };
    try {
      await axios.put(`${API}/feedback/${x.id}`, d);
      setRows((list) => list.map((y) => (y.id === x.id ? { ...y, ...d, statusAt: new Date().toISOString(), statusBy: t('xLoop.you') } : y)));
      setDraft((p) => { const n = { ...p }; delete n[x.id]; return n; });
      setSaved(x.id); setTimeout(() => setSaved(null), 1800);
    } catch (e) { setErr(errOf(e)); }
  };
  return (
    <div data-il-feedback>
      <div className="il-bar">
        <button type="button" className="sp-btn" onClick={load}><RefreshCw size={13} /> {t('xLoop.refresh')}</button>
        <div className="il-pills" role="group" aria-label={t('xLoop.colStatus')}>
          <button type="button" className={!statusF ? 'on' : ''} onClick={() => setStatusF('')}>{t('xLoop.all')} <em>{(rows || []).length}</em></button>
          {Object.keys(FS).map((k) => (
            <button key={k} type="button" className={`${statusF === k ? 'on' : ''} st-${k}`} onClick={() => setStatusF(statusF === k ? '' : k)} data-il-fbpill={k}>
              {t(FS[k])} <em>{count(k)}</em>
            </button>
          ))}
        </div>
        {tools.active && <button type="button" className="sp-btn" onClick={tools.clear}>{t('xLoop.clearFilters')}</button>}
        <span className="il-shown">{rows ? t('xLoop.shownOf', { n: shown.length, m: rows.length }) : ''}</span>
      </div>
      {err && <p className="sp-error">{err}</p>}
      {rows && !rows.length ? <p className="sp-sub">{t('xLoop.noFeedback')}</p> : (
        <div className="il-scroll">
          <table className="sp-table il-table il-fbtable">
            <thead><tr>
              <SortTh label={t('xLoop.colNo')} className="il-fb-no" {...tools.thProps('no')} />
              <SortTh label={t('xLoop.colFrom')} className="il-fb-from" {...tools.thProps('sender')} />
              <SortTh label={t('xLoop.colType')} className="il-fb-kind" {...tools.thProps('kind')} />
              <SortTh label={t('xLoop.colFeedback')} {...tools.thProps('note')} />
              <SortTh label={t('xLoop.colStatus')} className="il-fb-status" {...tools.thProps('status')} />
              <th className="il-fb-answer">{t('xLoop.colReply')}</th>
            </tr></thead>
            <tbody>
              {shown.map((x) => {
                const reply = draft[x.id] ?? x.reply ?? '';
                return (
                  <tr key={x.id} data-il-fb={x.id}>
                    <td className="il-fb-no"><b data-il-fbno={x.id}>{x.code || `#${x.id}`}</b></td>
                    <td className="il-fb-from">
                      <b>{x.sender || `#${x.userId}`}</b>
                      <small>{x.role || '—'}</small>
                      <small>{fmtDateTimeVN(x.createdAt)}</small>
                    </td>
                    <td className="il-fb-kind"><span className={`il-kind k-${x.kind}`}>{t(FK[x.kind] || x.kind)}</span></td>
                    <td className="il-msg il-fb-note">
                      <div className="il-fb-text">{x.note || <span className="il-muted">—</span>}</div>
                      {/* the files and screenshots the sender attached (03/11) — each opens in a new tab */}
                      {(x.files || []).length > 0 && (
                        <span className="fb-item-files" data-il-fbfiles={x.id}>
                          {/* an image opens large here; any other file opens (or downloads) in a new tab */}
                          {x.files.map((f) => (f.image ? (
                            <button key={f.id} type="button" className="il-fbimg" onClick={() => setPreview({ url: `${BACKEND_URL}/api/loop/feedback/files/${f.id}`, name: f.name })}
                              title={f.name} data-il-fbimg={f.id}>
                              <img src={`${BACKEND_URL}/api/loop/feedback/files/${f.id}`} alt={f.name} />
                            </button>
                          ) : (
                            <a key={f.id} href={`${BACKEND_URL}/api/loop/feedback/files/${f.id}`} target="_blank" rel="noopener noreferrer" title={f.name} data-il-fbfile={f.id}>
                              <Paperclip size={13} aria-hidden="true" /> {f.name}
                            </a>
                          )))}
                        </span>
                      )}
                      <small className="il-muted">{t('xLoop.fbScreen')}: <code>{x.screen || '/'}</code></small>
                    </td>
                    <td className="il-fb-status">
                      <select className={`il-input il-st st-${x.status}`} value={x.status} onChange={(e) => save(x, { status: e.target.value })} aria-label={t('xLoop.colStatus')} data-il-fbstatus={x.id}>
                        {Object.keys(FS).map((k) => <option key={k} value={k}>{t(FS[k])}</option>)}
                      </select>
                    </td>
                    <td className="il-fb-answer">
                      <textarea rows={3} value={reply} placeholder={t('xLoop.replyPh')} onChange={(e) => setDraft((p) => ({ ...p, [x.id]: e.target.value }))}
                        aria-label={t('xLoop.colReply')} data-il-fbreply={x.id} />
                      <div className="il-fb-save">
                        <button type="button" className="sp-btn primary" disabled={draft[x.id] == null || draft[x.id] === (x.reply || '')} onClick={() => save(x, {})} data-il-fbsave={x.id}>
                          {saved === x.id ? t('xLoop.saved') : t('xLoop.saveAnswer')}
                        </button>
                        {x.statusBy && x.statusAt && <small className="il-muted">{t('xLoop.lastBy', { name: x.statusBy, when: fmtDateTimeVN(x.statusAt) })}</small>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {preview && (
        <div className="fb-preview" role="dialog" aria-modal="true" aria-label={preview.name} onMouseDown={(e) => { if (e.target === e.currentTarget) setPreview(null); }} data-il-preview>
          <div className="fb-preview-bar"><span>{preview.name}</span>
            <a className="fb-x" href={preview.url} target="_blank" rel="noopener noreferrer" title={t('xLoop.newTab')}><ExternalLink size={16} /></a>
            <button type="button" className="fb-x" onClick={() => setPreview(null)} aria-label={t('xLoop.close')} data-il-preview-close><X size={18} /></button></div>
          <img src={preview.url} alt={preview.name} />
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { axios.get(`${API}/config`).then((r) => setRows(r.data.rows)).catch((e) => setErr(errOf(e))); }, []);
  const flip = async (x) => {
    const on = !x.enabled;
    setRows((list) => list.map((y) => (y.key === x.key ? { ...y, enabled: on } : y)));
    try { await axios.put(`${API}/config/${encodeURIComponent(x.key)}`, { enabled: on }); }
    catch (e) { setErr(errOf(e)); setRows((list) => list.map((y) => (y.key === x.key ? { ...y, enabled: !on } : y))); }
  };
  const part = (kind, title) => (
    <>
      <h4>{title}</h4>
      {(rows || []).filter((x) => x.kind === kind).map((x) => (
        <label key={x.key} className="il-set-row" data-il-set={x.key}>
          <input type="checkbox" checked={!!x.enabled} onChange={() => flip(x)} />
          <div><b>{x.label}</b>{x.note && <small>{x.note}</small>}</div>
          <span className={`il-chip ${x.enabled ? 'fixed' : 'ignored'}`}>{x.enabled ? t('xLoop.on') : t('xLoop.off')}</span>
        </label>
      ))}
    </>
  );
  return (
    <div className="il-set" data-il-settings>
      <p className="sp-note">{t('xLoop.setIntro')}</p>
      {err && <p className="sp-error">{err}</p>}
      {part('error', t('xLoop.setErrors'))}
      {part('feedback', t('xLoop.setFeedback'))}
    </div>
  );
}

export default function ImprovementLoop() {
  const { t } = useTranslation();
  // ?tab=feedback — the Admins' bell line for a new feedback opens it there (Max 03/11)
  const [tab, setTab] = useState(() => (['errors', 'feedback', 'settings'].includes(new URLSearchParams(window.location.search).get('tab'))
    ? new URLSearchParams(window.location.search).get('tab') : 'errors'));
  // both servers write to one database: production's rows are the ones that matter, staging's are kept apart
  const [envSel, setEnvSel] = useState('');
  return (
    <div className="pa-page">
      <Header />
      <main className="pa-main">
        <div className="mh-titlerow">
          <div className="pa-title">
            <h2>{t('xLoop.title')}</h2>
            <p>{t('xLoop.sub')}</p>
          </div>
          <label className="il-bar" style={{ margin: 0 }}>
            <span className="sp-sub">{t('xLoop.env')}</span>
            <select value={envSel} onChange={(e) => setEnvSel(e.target.value)} data-il-env>
              <option value="">{t('xLoop.envThis')}</option>
              <option value="production">production</option>
              <option value="staging">staging</option>
            </select>
          </label>
        </div>
        <div className="pa-tabs view-tabs" role="tablist">
          <button type="button" className={`pa-tab ${tab === 'errors' ? 'active' : ''}`} onClick={() => setTab('errors')} data-il-tab="errors">
            <AlertTriangle size={15} strokeWidth={2.2} /> {t('xLoop.tabErrors')}
          </button>
          <button type="button" className={`pa-tab ${tab === 'feedback' ? 'active' : ''}`} onClick={() => setTab('feedback')} data-il-tab="feedback">
            <MessageSquarePlus size={15} strokeWidth={2.2} /> {t('xLoop.tabFeedback')}
          </button>
          <button type="button" className={`pa-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')} data-il-tab="settings">
            <SlidersHorizontal size={15} strokeWidth={2.2} /> {t('xLoop.tabSettings')}
          </button>
        </div>
        {tab === 'errors' && <ErrorsTab envSel={envSel} />}
        {tab === 'feedback' && <FeedbackTab envSel={envSel} />}
        {tab === 'settings' && <SettingsTab />}
      </main>
    </div>
  );
}
